import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  Loader2,
  LogOut,
  Trash2,
  MessageCircle,
  Users,
  MessageSquareHeart,
  Phone,
  Pencil,
  ArrowUp,
  ArrowDown,
  UserCheck,
  Hourglass,
  Gift,
  Search,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { LOGO_URL } from "../lib/troy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "troy_admin_token";

// Token em memória = fonte da verdade (funciona mesmo com localStorage bloqueado:
// modo privado / WebView de apps). localStorage é só persistência best-effort.
let memoryToken = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();

const storage = {
  get: () => memoryToken,
  set: (v) => {
    memoryToken = v;
    try {
      localStorage.setItem(TOKEN_KEY, v);
    } catch {}
  },
  remove: () => {
    memoryToken = null;
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {}
  },
};

const authHeaders = () => {
  const t = storage.get();
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const waReplyLink = (phone, name) => {
  const digits = String(phone).replace(/\D/g, "");
  const full = digits.startsWith("55") ? digits : `55${digits}`;
  const msg = `Olá ${name}! Aqui é da Troy Transportes. Recebemos seu feedback e queremos agradecer pelo contato. `;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
};

const waDriverWelcomeLink = (phone, name) => {
  const digits = String(phone).replace(/\D/g, "");
  const full = digits.startsWith("55") ? digits : `55${digits}`;
  const msg = `Olá ${name}! Aqui é da *Troy Transportes* 🚗\n\nRecebemos seu cadastro de motorista parceiro e ficamos muito felizes com seu interesse!\n\nPara finalizar sua ativação, precisamos confirmar alguns dados e te adicionar ao grupo de corridas. Pode falar agora?`;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
};

export default function Admin() {
  const [token, setToken] = useState(() => storage.get());

  useEffect(() => {
    document.title = "Painel Admin — Troy Transportes";
  }, []);

  const logout = () => {
    storage.remove();
    setToken(null);
  };

  return (
    <div className="bg-[#08080A] text-white min-h-screen">
      {token ? <Dashboard onLogout={logout} /> : <Login onLogin={setToken} />}
    </div>
  );
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      storage.set(data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao entrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl bg-[#121215] border border-[#FFCC00]/15 p-8 text-center"
      >
        <div className="relative mx-auto mb-5 w-28 h-28">
          <div className="absolute -inset-3 bg-[#FF3B30]/25 blur-2xl rounded-full" />
          <div className="absolute inset-0 rounded-[1.4rem] bg-[radial-gradient(circle,rgba(255,204,0,0.3),transparent_70%)]" />
          <img
            src={LOGO_URL}
            alt="Troy"
            className="relative w-28 h-28 rounded-[1.4rem] object-contain bg-black/70 border border-[#FFCC00]/30 p-2 drop-shadow-[0_0_24px_rgba(255,59,48,0.45)]"
          />
        </div>
        <h1 className="font-display text-2xl font-black mb-1">Painel Admin</h1>
        <p className="text-white/50 text-sm mb-6">Acesso restrito da Troy Transportes</p>
        <div className="relative">
          <Lock className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
          <Input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha de acesso"
            className="bg-[#1C1C22] border-white/10 h-12 rounded-xl pl-11 text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-bold py-3.5 px-8 rounded-xl hover:bg-[#E6B800] transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("feedbacks");
  const [feedbacks, setFeedbacks] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [spins, setSpins] = useState(null);

  const handleAuthError = useCallback(
    (err) => {
      if (err?.response?.status === 401) {
        toast.error("Sessão expirada. Entre novamente.");
        onLogout();
        return true;
      }
      return false;
    },
    [onLogout]
  );

  const load = useCallback(async () => {
    try {
      const [fb, dr, sp] = await Promise.all([
        axios.get(`${API}/feedbacks`, authHeaders()),
        axios.get(`${API}/drivers`, authHeaders()),
        axios.get(`${API}/roulette/spins`, authHeaders()),
      ]);
      setFeedbacks(fb.data);
      setDrivers(dr.data);
      setSpins(sp.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erro ao carregar dados.");
    }
  }, [handleAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteFeedback = async (id) => {
    if (!window.confirm("Excluir este feedback?")) return;
    try {
      await axios.delete(`${API}/feedbacks/${id}`, authHeaders());
      setFeedbacks((list) => list.filter((f) => f.id !== id));
      toast.success("Feedback excluído.");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erro ao excluir feedback.");
    }
  };

  const deleteDriver = async (id) => {
    if (!window.confirm("Excluir este cadastro de motorista?")) return;
    try {
      await axios.delete(`${API}/drivers/${id}`, authHeaders());
      setDrivers((list) => list.filter((d) => d.id !== id));
      toast.success("Cadastro excluído.");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erro ao excluir cadastro.");
    }
  };

  const deleteSpin = async (id) => {
    if (!window.confirm("Excluir este desconto da roleta?")) return;
    try {
      await axios.delete(`${API}/roulette/spins/${id}`, authHeaders());
      setSpins((list) => list.filter((s) => s.id !== id));
      toast.success("Desconto excluído. O número poderá girar novamente.");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erro ao excluir desconto.");
    }
  };

  const patchDriver = async (id, updates) => {
    try {
      const { data } = await axios.patch(`${API}/drivers/${id}`, updates, authHeaders());
      setDrivers((list) => list.map((d) => (d.id === id ? data : d)));
      return true;
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erro ao salvar alteração.");
      return false;
    }
  };

  const reorderWaitlist = async (ids) => {
    try {
      await axios.post(`${API}/drivers/waitlist-order`, { ids }, authHeaders());
      setDrivers((list) =>
        list.map((d) => {
          const idx = ids.indexOf(d.id);
          return idx >= 0 ? { ...d, queue_position: idx } : d;
        })
      );
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erro ao reordenar lista.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="relative overflow-hidden rounded-3xl border border-[#FFCC00]/20 bg-gradient-to-r from-[#FFCC00]/10 via-[#121215] to-[#121215] p-5 sm:p-7 mb-8">
        <img
          src={LOGO_URL}
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -right-8 -top-8 w-48 h-48 object-contain opacity-[0.08]"
        />
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="absolute -inset-2 bg-[#FF3B30]/25 blur-xl rounded-full" />
              <img
                src={LOGO_URL}
                alt="Troy"
                className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-contain bg-black/70 border border-[#FFCC00]/30 p-1.5 drop-shadow-[0_0_20px_rgba(255,59,48,0.45)]"
              />
            </div>
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-black leading-none">
                Painel <span className="text-[#FFCC00]">Admin</span>
              </h1>
              <p className="text-white/50 text-xs sm:text-sm mt-1.5 uppercase tracking-[0.2em] font-semibold">
                Troy Transportes
              </p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white border border-[#FFCC00]/15 rounded-xl px-4 py-2 transition-colors bg-black/30"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-8">
        <TabButton
          active={tab === "feedbacks"}
          onClick={() => setTab("feedbacks")}
          icon={MessageSquareHeart}
          label={`Feedbacks${feedbacks ? ` (${feedbacks.length})` : ""}`}
          testid="admin-tab-feedbacks"
        />
        <TabButton
          active={tab === "drivers"}
          onClick={() => setTab("drivers")}
          icon={Users}
          label={`Motoristas${drivers ? ` (${drivers.length})` : ""}`}
          testid="admin-tab-drivers"
        />
        <TabButton
          active={tab === "roleta"}
          onClick={() => setTab("roleta")}
          icon={Gift}
          label={`Roleta${spins ? ` (${spins.length})` : ""}`}
          testid="admin-tab-roleta"
        />
      </div>

      {tab === "feedbacks" && (
        <FeedbackList feedbacks={feedbacks} onDelete={deleteFeedback} />
      )}
      {tab === "drivers" && (
        <DriversManager
          drivers={drivers}
          onPatch={patchDriver}
          onReorder={reorderWaitlist}
          onDelete={deleteDriver}
        />
      )}
      {tab === "roleta" && <SpinsList spins={spins} onDelete={deleteSpin} />}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors ${
        active
          ? "bg-[#FFCC00] text-black"
          : "bg-[#18181C] text-white/60 border border-[#FFCC00]/15 hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function FeedbackList({ feedbacks, onDelete }) {
  if (!feedbacks)
    return <p className="text-white/40 text-sm" data-testid="admin-feedbacks-loading">Carregando feedbacks...</p>;
  if (feedbacks.length === 0)
    return <p className="text-white/40 text-sm" data-testid="admin-feedbacks-empty">Nenhum feedback recebido ainda.</p>;

  return (
    <div className="space-y-4" data-testid="admin-feedbacks-list">
      {feedbacks.map((f) => (
        <div
          key={f.id}
          data-testid={`admin-feedback-item-${f.id}`}
          className="rounded-2xl bg-[#121215] border border-[#FFCC00]/15 p-5"
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <p className="font-bold">{f.name}</p>
              <p className="text-white/40 text-xs flex items-center gap-1.5 mt-0.5">
                <Phone className="w-3 h-3" /> {f.phone} · {fmtDate(f.created_at)}
              </p>
            </div>
            <div className="flex gap-2">
              <a
                href={waReplyLink(f.phone, f.name)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`admin-feedback-reply-${f.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#25D366]/15 text-[#25D366] px-3.5 py-2 rounded-lg hover:bg-[#25D366]/25 transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Responder
              </a>
              <button
                onClick={() => onDelete(f.id)}
                data-testid={`admin-feedback-delete-${f.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#FF3B30]/15 text-[#FF3B30] px-3.5 py-2 rounded-lg hover:bg-[#FF3B30]/25 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Excluir
              </button>
            </div>
          </div>
          <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{f.message}</p>
        </div>
      ))}
    </div>
  );
}


const isAtivo = (d) => d.status === "ativo";

const EDIT_FIELDS = [
  ["name", "Nome"],
  ["whatsapp", "WhatsApp"],
  ["city", "Cidade"],
  ["neighborhood", "Bairro/Região"],
  ["vehicle_type", "Tipo de veículo"],
  ["car_model", "Modelo do veículo"],
  ["car_plate", "Placa"],
  ["available_days", "Dias disponíveis"],
  ["available_hours", "Horários disponíveis"],
];

function DriversManager({ drivers, onPatch, onReorder, onDelete }) {
  const [editing, setEditing] = useState(null);

  if (!drivers)
    return <p className="text-white/40 text-sm" data-testid="admin-drivers-loading">Carregando motoristas...</p>;

  const ativos = drivers.filter(isAtivo);
  const espera = drivers
    .filter((d) => !isAtivo(d))
    .sort(
      (a, b) =>
        (a.queue_position ?? 99999) - (b.queue_position ?? 99999) ||
        new Date(a.created_at) - new Date(b.created_at)
    );

  const moveInQueue = (idx, dir) => {
    const ids = espera.map((d) => d.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    onReorder(ids);
  };

  return (
    <div data-testid="admin-drivers-manager">
      <div className="grid grid-cols-3 gap-3 mb-8">
        <StatCard testid="stat-ativos" color="#25D366" label="Motoristas Ativos" value={ativos.length} />
        <StatCard testid="stat-espera" color="#FFCC00" label="Lista de Espera" value={espera.length} />
        <StatCard testid="stat-total" color="#FFFFFF" label="Total" value={drivers.length} />
      </div>

      <SectionHeader icon={UserCheck} color="#25D366" title="Motoristas Ativos" count={ativos.length} testid="section-ativos" />
      {ativos.length === 0 ? (
        <p className="text-white/40 text-sm mb-8" data-testid="ativos-empty">
          Nenhum motorista ativo ainda. Use "Mover para Ativos" na lista de espera.
        </p>
      ) : (
        <div className="space-y-3 mb-8">
          {ativos.map((d) => (
            <DriverCard
              key={d.id}
              d={d}
              accent="#25D366"
              onEdit={() => setEditing(d)}
              onDelete={onDelete}
              actionBtn={
                <ActionButton
                  testid={`move-espera-${d.id}`}
                  color="#FFCC00"
                  icon={Hourglass}
                  label="Mover para Lista de Espera"
                  onClick={() => onPatch(d.id, { status: "espera" })}
                />
              }
            />
          ))}
        </div>
      )}

      <SectionHeader icon={Hourglass} color="#FFCC00" title="Lista de Espera" count={espera.length} testid="section-espera" />
      {espera.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="espera-empty">Ninguém na lista de espera.</p>
      ) : (
        <div className="space-y-3">
          {espera.map((d, idx) => (
            <DriverCard
              key={d.id}
              d={d}
              accent="#FFCC00"
              position={idx + 1}
              onEdit={() => setEditing(d)}
              onDelete={onDelete}
              queueControls={
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveInQueue(idx, -1)}
                    disabled={idx === 0}
                    data-testid={`queue-up-${d.id}`}
                    aria-label="Subir na lista"
                    className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-[#FFCC00] disabled:opacity-30 transition-colors"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveInQueue(idx, 1)}
                    disabled={idx === espera.length - 1}
                    data-testid={`queue-down-${d.id}`}
                    aria-label="Descer na lista"
                    className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-[#FFCC00] disabled:opacity-30 transition-colors"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
              }
              actionBtn={
                <ActionButton
                  testid={`move-ativo-${d.id}`}
                  color="#25D366"
                  icon={UserCheck}
                  label="Mover para Ativos"
                  onClick={() => onPatch(d.id, { status: "ativo" })}
                />
              }
            />
          ))}
        </div>
      )}

      <EditDriverDialog
        driver={editing}
        onClose={() => setEditing(null)}
        onSave={async (id, updates) => {
          const ok = await onPatch(id, updates);
          if (ok) {
            toast.success("Cadastro atualizado.");
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-[#121215] border border-[#FFCC00]/15 p-4 text-center"
    >
      <p className="font-display text-3xl font-black" style={{ color }}>{value}</p>
      <p className="text-white/50 text-[11px] sm:text-xs mt-1 uppercase tracking-wider font-semibold">{label}</p>
    </div>
  );
}

function SectionHeader({ icon: Icon, color, title, count, testid }) {
  return (
    <div className="flex items-center gap-2 mb-4" data-testid={testid}>
      <Icon className="w-5 h-5" style={{ color }} />
      <h2 className="font-display text-lg font-black">{title}</h2>
      <span
        className="text-xs font-bold px-2 py-0.5 rounded-full border"
        style={{ color, borderColor: `${color}40`, backgroundColor: `${color}10` }}
      >
        {count}
      </span>
    </div>
  );
}

function ActionButton({ onClick, icon: Icon, label, color, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg transition-colors"
      style={{ backgroundColor: `${color}20`, color }}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function DriverCard({ d, accent, position, queueControls, actionBtn, onEdit, onDelete }) {
  return (
    <div
      data-testid={`admin-driver-item-${d.id}`}
      className="rounded-2xl bg-[#121215] border p-4 sm:p-5"
      style={{ borderColor: `${accent}30`, borderLeftWidth: 3, borderLeftColor: accent }}
    >
      <div className="flex items-start gap-3">
        {position ? (
          <span
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black"
            style={{ backgroundColor: `${accent}20`, color: accent }}
            data-testid={`queue-pos-${d.id}`}
          >
            {position}
          </span>
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-bold">{d.name}</p>
              <p className="text-white/40 text-xs flex items-center gap-1.5 mt-0.5">
                <Phone className="w-3 h-3" /> {d.whatsapp}
              </p>
            </div>
            {queueControls}
          </div>
          <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-white/60">
            <Info label="Bairro/Região" value={[d.neighborhood, d.city].filter(Boolean).join(" — ")} />
            <Info label="Veículo" value={[d.vehicle_type, d.car_model, d.car_color, d.car_year].filter(Boolean).join(" · ")} />
            <Info label="Placa" value={d.car_plate} />
            <Info label="Dias disponíveis" value={d.available_days} />
            <Info label="Horários" value={d.available_hours} />
            <Info label="Disponibilidade" value={d.availability} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {actionBtn}
            <button
              onClick={onEdit}
              data-testid={`admin-driver-edit-${d.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/5 text-white/80 border border-white/10 px-3.5 py-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> Editar cadastro
            </button>
            <a
              href={waDriverWelcomeLink(d.whatsapp, d.name)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`admin-driver-contact-${d.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#25D366]/15 text-[#25D366] px-3.5 py-2 rounded-lg hover:bg-[#25D366]/25 transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
            <button
              onClick={() => onDelete(d.id)}
              data-testid={`admin-driver-delete-${d.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#FF3B30]/15 text-[#FF3B30] px-3.5 py-2 rounded-lg hover:bg-[#FF3B30]/25 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Excluir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditDriverDialog({ driver, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (driver) {
      const initial = {};
      EDIT_FIELDS.forEach(([k]) => (initial[k] = driver[k] || ""));
      setForm(initial);
    }
  }, [driver]);

  if (!driver) return null;

  const submit = async (e) => {
    e.preventDefault();
    const updates = {};
    Object.entries(form).forEach(([k, v]) => {
      if (String(v).trim() !== "") updates[k] = String(v).trim();
    });
    if (!updates.name || !updates.whatsapp) {
      toast.error("Nome e WhatsApp são obrigatórios.");
      return;
    }
    setSaving(true);
    await onSave(driver.id, updates);
    setSaving(false);
  };

  return (
    <Dialog open={!!driver} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="edit-driver-dialog"
        className="bg-[#121215] border-[#FFCC00]/20 text-white max-h-[85vh] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black">Editar cadastro — {driver.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EDIT_FIELDS.map(([key, label]) => (
              <div key={key} className={key === "name" ? "sm:col-span-2" : ""}>
                <Label htmlFor={`edit-${key}`} className="text-white/70 text-xs font-semibold mb-1.5 block">
                  {label}
                </Label>
                <Input
                  id={`edit-${key}`}
                  data-testid={`edit-field-${key}`}
                  value={form[key] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="bg-[#1C1C22] border-white/10 h-10 rounded-lg text-white text-sm focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                />
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="edit-driver-cancel"
              className="px-5 py-2.5 rounded-lg text-sm font-bold bg-white/5 border border-white/10 text-white/70 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-driver-save"
              className="px-5 py-2.5 rounded-lg text-sm font-bold bg-[#FFCC00] text-black hover:bg-[#E6B800] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Salvar alterações
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  if (!value) return null;
  return (
    <p className="truncate">
      <span className="text-white/35">{label}:</span> {value}
    </p>
  );
}

function SpinsList({ spins, onDelete }) {
  const [query, setQuery] = useState("");

  if (!spins)
    return <p className="text-white/40 text-sm" data-testid="admin-spins-loading">Carregando giros da roleta...</p>;

  const q = query.replace(/\D/g, "");
  const filtered = q
    ? spins.filter((s) => String(s.phone).includes(q))
    : spins;

  return (
    <div data-testid="admin-spins-list">
      <div className="relative mb-5 max-w-sm">
        <Search className="w-4 h-4 text-white/35 absolute left-4 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="spins-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar pelo número do cliente..."
          className="bg-[#1C1C22] border-white/10 h-11 rounded-xl pl-11 text-white text-sm placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
        />
      </div>

      {spins.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="admin-spins-empty">Ninguém girou a roleta ainda.</p>
      ) : filtered.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="admin-spins-no-match">Nenhum giro encontrado para este número.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <div
              key={s.id}
              data-testid={`admin-spin-item-${s.id}`}
              className="rounded-2xl bg-[#121215] border border-[#FFCC00]/15 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3"
            >
              <div>
                <p className="font-bold">{s.name || "Sem nome"}</p>
                <p className="text-white/40 text-xs flex items-center gap-1.5 mt-0.5">
                  <Phone className="w-3 h-3" /> {s.phone} · {fmtDate(s.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  data-testid={`spin-discount-${s.id}`}
                  className="font-display text-xl font-black px-4 py-2 rounded-xl bg-[#FFCC00]/10 text-[#FFCC00] border border-[#FFCC00]/30"
                >
                  {s.discount}% OFF
                </span>
                <button
                  onClick={() => onDelete(s.id)}
                  data-testid={`admin-spin-delete-${s.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#FF3B30]/15 text-[#FF3B30] px-3.5 py-2.5 rounded-lg hover:bg-[#FF3B30]/25 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
